import type { DatasetCity, LandingData, UnsubscribeInfo } from "@noko-city/shared";

import {
  errorMessageOf,
  isCitiesResponse,
  isLandingData,
  isUnsubscribeInfo,
} from "@/lib/api-response";

export type {
  CityLiveFlightDirection,
  CityPage,
  CityPageCategoryMix,
  CityPageCategoryShare,
  CityPageInstagramPost,
  CityPageLive,
  CityPageSectionImage,
  CityPageVenue,
  DatasetCity,
  LandingCity,
  LandingData,
  UnsubscribeInfo,
} from "@noko-city/shared";

export async function fetchLanding(signal?: AbortSignal): Promise<LandingData> {
  const res = await fetch("/api/landing", { signal });
  if (!res.ok) throw new Error(`GET /api/landing failed: ${res.status}`);
  const data: unknown = await res.json();
  if (!isLandingData(data)) throw new Error("Invalid API response");
  return data;
}

/**
 * The directory payload the served document carries (`noko-city-index`), or null
 * when this document has none — the dev server serves the bare shell. A document
 * built for deployment always carries one, so the home page reads the city list
 * and its weather from itself instead of asking the API.
 */
export function readEmbeddedLanding(): LandingData | null {
  const el = document.getElementById("noko-city-index");
  if (!el?.textContent) return null;
  try {
    const parsed: unknown = JSON.parse(el.textContent);
    return isLandingData(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    return errorMessageOf(await res.json()) ?? fallback;
  } catch {
    return fallback;
  }
}

/** City names (+ state) for one ISO alpha-2 country, from the backend's dataset. */
export async function fetchCities(country: string, signal?: AbortSignal): Promise<DatasetCity[]> {
  const res = await fetch(`/api/landing/cities?country=${encodeURIComponent(country)}`, { signal });
  if (!res.ok) throw new Error(`GET /api/landing/cities failed: ${res.status}`);
  const data: unknown = await res.json();
  if (!isCitiesResponse(data)) throw new Error("Invalid API response");
  return data.cities;
}

export async function submitCityRequest(
  city: string,
  country: string,
  email: string,
  turnstileToken?: string,
): Promise<void> {
  const res = await fetch("/api/city-requests", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ city, country, email, turnstileToken }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Submit failed: ${res.status}`));
}

export async function verifyCityRequest(city: string, email: string, code: string): Promise<void> {
  const res = await fetch("/api/city-requests/verify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ city, email, code }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Verification failed: ${res.status}`));
}

export async function fetchUnsubscribeInfo(
  token: string,
  signal?: AbortSignal,
): Promise<UnsubscribeInfo> {
  const res = await fetch(`/api/unsubscribe/${encodeURIComponent(token)}`, { signal });
  if (!res.ok) {
    throw new Error(await errorMessage(res, `GET /api/unsubscribe failed: ${res.status}`));
  }
  const data: unknown = await res.json();
  if (!isUnsubscribeInfo(data)) throw new Error("Invalid API response");
  return data;
}

export async function submitUnsubscribe(token: string, turnstileToken: string): Promise<void> {
  const res = await fetch("/api/unsubscribe", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, turnstileToken }),
  });
  if (!res.ok) throw new Error(await errorMessage(res, `Unsubscribe failed: ${res.status}`));
}

/** Why a contact form message was refused, so the page can say something useful. */
export type CityContactFailure = "captcha" | "invalid" | "throttled" | "unavailable" | "unknown";

export type CityContactResult = { ok: true } | { ok: false; reason: CityContactFailure };

/**
 * Send one city-guide contact form message.
 *
 * The slug is the only identity that travels: the API resolves the city's inbox
 * from it, and no response (or URL, or header) names that inbox. A refusal
 * carries a reason instead of a thrown error, because the page shows different
 * copy for a failed check, a rejected field and a rate limit.
 */
export async function submitCityContact(input: {
  slug: string;
  name: string;
  email: string;
  phone?: string;
  message: string;
  turnstileToken?: string;
  website?: string;
  elapsedMs?: number;
  locale?: string;
}): Promise<CityContactResult> {
  const res = await fetch("/api/city-contact", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (res.ok) return { ok: true };
  if (res.status === 400) {
    // The only two 400s are a failed captcha and a rejected field; the server's
    // message is what tells them apart.
    const message = await errorMessage(res, "");
    return { ok: false, reason: /captcha/i.test(message) ? "captcha" : "invalid" };
  }
  if (res.status === 429) return { ok: false, reason: "throttled" };
  if (res.status === 404) return { ok: false, reason: "invalid" };
  return { ok: false, reason: res.status >= 500 ? "unavailable" : "unknown" };
}
